import { Section } from '@salecycle/experimental/Section/Section';
import { MainArea, Page, Spinner, TopArea, Error } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CampaignTabs } from './components/CampaignTabs';
import { CampaignFooterArea } from './components/CampaignFooterArea';
import { CampaignHeaderBar } from './components/CampaignHeaderBar';
import { CampaignEditorStateProvider } from './state/CampaignEditorStateProvider';
import { useParams, useRouteMatch } from 'react-router';
import { AuthorisedRoute } from 'core/router/AuthorisedRoute';
import { CampaignEditorTab } from './hooks/useActiveTab';
import { AudienceTab } from './components/AudienceTab/AudienceTab';
import { ContentTab } from './components/ContentTab/ContentTab';
import { SchedulingTab } from './components/SchedulingTab/SchedulingTab';
import { CampaignDrawer } from './components/CampaignDrawer/CampaignDrawer';
import { CampaignExitPrompt } from './components/CampaignExitPrompt';
import { CampaignNotice } from './components/CampaignNotice';
import { useGetTemplates } from 'server/templates/queries/getTemplates/useGetTemplates';
import { useGetSubjectLines } from 'server/subject-lines/queries/getSubjectLines/useGetSubjectLines';
import { useGetBulkRetargetingCampaignQuery } from '../queries/useGetBulkRetargetingCampaign';
import { mapCampaignToState } from './infrastructure/mapCampaignToState';

const SpinnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CampaignBody = styled.div`
  flex-grow: 1;
  display: flex;
  overflow-y: auto;
`;

const FlexCenter = styled.div`
  padding: ${({ theme }) => theme.designToken.spacing8};
  margin: 0 auto;
`;

const ContentBody = styled.div`
  width: 1000px;
  height: 100%;
  display: flex;
  gap: ${({ theme }) => theme.designToken.spacing6};
  flex-direction: column;
`;

export const CampaignEditor = () => {
  const { t } = useTranslation('campaigns');
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();
  const { path } = useRouteMatch();

  const { data, isError, isLoading } = useGetBulkRetargetingCampaignQuery({ siteId, campaignId });
  const { data: templateData, isLoading: templateIsLoading } = useGetTemplates(siteId);
  const { data: subjectLinesData, isLoading: subjectLinesIsLoading } = useGetSubjectLines({ siteId });

  if ((isLoading || templateIsLoading || subjectLinesIsLoading) && !isError) {
    return (
      <MainArea>
        <TopArea>
          <CampaignHeaderBar isError={isError} />
        </TopArea>
        <Page padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </Page>
      </MainArea>
    );
  }

  const state = mapCampaignToState(data, templateData, subjectLinesData, t);

  return (
    <CampaignEditorStateProvider
      key={`provider-${state.status}-${campaignId}`}
      status={state.status}
      readOnly={state.readOnly}
      campaign={state.campaign}
    >
      <MainArea>
        <TopArea>
          <CampaignHeaderBar isError={isError} />
        </TopArea>
        <Page padding={0} style={{ display: 'flex', flexDirection: 'column' }}>
          {!isLoading && isError && <Error heading={t('onsite.errorLoadingCampaigns')} />}
          {!isLoading && !isError && (
            <>
              <Section alignSelf="center">
                <CampaignTabs />
              </Section>
              <CampaignBody>
                <FlexCenter>
                  <ContentBody>
                    <CampaignNotice />
                    <AuthorisedRoute exact path={`${path}/${CampaignEditorTab.Audience}`} component={AudienceTab} />
                    <AuthorisedRoute exact path={`${path}/${CampaignEditorTab.Content}`} component={ContentTab} />
                    <AuthorisedRoute exact path={`${path}/${CampaignEditorTab.Scheduling}`} component={SchedulingTab} />
                  </ContentBody>
                </FlexCenter>
              </CampaignBody>
              <CampaignFooterArea />
              <CampaignDrawer />
              <CampaignExitPrompt />
            </>
          )}
        </Page>
      </MainArea>
    </CampaignEditorStateProvider>
  );
};
